import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  clearChunkRecoveryMarker,
  isChunkLoadError,
  shouldAttemptChunkRecovery,
} from "@/lib/chunk-recovery";

type Props = { children: ReactNode };

type State = { error: Error | null };

function RouteErrorFallback({
  error,
  onReload,
}: {
  error: Error;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const showDetails = import.meta.env.DEV && Boolean(error.message);

  return (
    <div className="p-6 font-sans">
      <p className="mb-3 text-foreground">{t("routeErrorBoundary.summary")}</p>
      {showDetails ? (
        <pre className="mb-3 max-w-[min(100%,720px)] overflow-auto rounded-lg bg-muted p-3 text-[13px] text-foreground">
          {error.message}
        </pre>
      ) : null}
      <Button type="button" variant="outline" onClick={onReload}>
        {t("routeErrorBoundary.reload")}
      </Button>
    </div>
  );
}

/**
 * Prevents a single route render error from unmounting the entire app (blank screen).
 *
 * Also acts as the fallback recovery for chunk-load errors that bypass `vite:preloadError`
 * (registered in `src/main.tsx`) — e.g. a deferred `import()` inside an event handler whose
 * rejection only surfaces during a child render. Shares the same rate-limit marker so the two
 * paths never double-reload.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  componentDidMount(): void {
    /** Successful mount (no error in initial render) means a previous chunk-recovery reload
     * actually worked — drop the marker so an unrelated future failure isn't blocked by it. */
    if (this.state.error === null) clearChunkRecoveryMarker();
  }

  componentDidUpdate(_: Readonly<Props>, prevState: Readonly<State>): void {
    /** Same idea for the manual reload → children render OK path. */
    if (prevState.error !== null && this.state.error === null) clearChunkRecoveryMarker();
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error("[RouteErrorBoundary]", error, info.componentStack);
    }
    this.tryRecoverChunkLoadError(error);
  }

  private tryRecoverChunkLoadError(error: Error): boolean {
    if (!isChunkLoadError(error)) return false;
    if (!shouldAttemptChunkRecovery()) return false;
    window.location.reload();
    return true;
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <RouteErrorFallback
          error={this.state.error}
          onReload={() => {
            window.location.reload();
          }}
        />
      );
    }
    return this.props.children;
  }
}
